import { useState } from "react";
import { Reveal, SectionHeading, scrollToId } from "./decor";
import { ASSETS, TOUR_DATES } from "../data/siteData";
import { useLang } from "../i18n";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const FILTER_IDS = ["all", "confirmed", "festival", "open"];

const STATUS_STYLES = {
  confirmed: "border-red-600/50 bg-red-600/10 text-red-500",
  festival: "border-amber-500/50 bg-amber-500/10 text-amber-400",
  open: "border-green-500/50 bg-green-500/10 text-green-400",
};

export const TourDates = () => {
  const [filter, setFilter] = useState("all");
  const { t } = useLang();
  const dates = TOUR_DATES.filter((d) => filter === "all" || d.status === filter);

  return (
    <section id="tour" data-testid="tour-section" className="relative py-24 sm:py-32 bg-[#0D0D10] overflow-hidden">
      <div className="absolute inset-0">
        <img src={ASSETS.tourBg} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D10] via-[#0D0D10]/75 to-[#0D0D10]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading kicker={t.tour.kicker} title={t.tour.title} testId="tour-heading" />

        <div className="flex flex-wrap gap-2 mb-10" data-testid="tour-filter-tabs">
          {FILTER_IDS.map((id) => (
            <button
              key={id}
              data-testid={`tour-filter-${id}`}
              onClick={() => setFilter(id)}
              className={`font-mono-tech text-xs tracking-[0.2em] uppercase px-4 py-2 border transition-colors duration-300 ${
                filter === id
                  ? "border-red-600 bg-red-600 text-white"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"
              }`}
            >
              {t.tour.filters[id]}
            </button>
          ))}
        </div>

        <div data-testid="tour-dates-list" className="border-t border-zinc-800">
          {dates.length === 0 && (
            <p data-testid="tour-empty-state" className="border-b border-zinc-800 py-10 px-4 font-mono-tech text-sm tracking-[0.15em] uppercase text-zinc-500">
              {t.tour.emptyPre}{" "}
              <button data-testid="tour-empty-cta" onClick={() => scrollToId("booking")} className="text-red-500 hover:text-red-400 underline underline-offset-4">
                {t.tour.emptyCta}
              </button>
              .
            </p>
          )}
          {dates.map((d, i) => {
            const [y, m, dayNum] = d.iso.split("-");
            const day = d.tbc ? "TBC" : dayNum;
            const month = MONTHS[Number(m) - 1];
            const venue = d.venue === "Venue TBA" ? t.tour.venueTba : d.venue;
            return (
              <Reveal key={d.iso} delay={i * 0.05}>
                <div
                  data-testid={`tour-date-row-${d.iso}`}
                  className="grid grid-cols-[64px_1fr] sm:grid-cols-[80px_1fr_auto] items-center gap-5 sm:gap-8 border-b border-zinc-800 py-6 px-2 sm:px-4 hover:bg-white/[0.03] transition-colors duration-300 group"
                >
                  <div className="text-center border-r border-zinc-800 pr-4">
                    <p className="font-display text-4xl sm:text-5xl text-white leading-none group-hover:text-red-600 transition-colors duration-300">{day}</p>
                    <p className="font-mono-tech text-xs tracking-[0.2em] text-zinc-500 mt-1">{month} {y}</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl sm:text-3xl tracking-wide text-white uppercase">
                      {d.city} <span className="text-zinc-600">/ {d.country}</span>
                    </p>
                    <p className="font-mono-tech text-xs sm:text-sm tracking-[0.15em] text-zinc-400 uppercase mt-1">
                      {venue} — {d.type}
                    </p>
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex sm:flex-col items-center sm:items-end gap-3">
                    <span
                      data-testid={`tour-status-${d.iso}`}
                      className={`font-mono-tech text-[11px] tracking-[0.2em] uppercase border px-3 py-1.5 ${STATUS_STYLES[d.status]}`}
                    >
                      {t.tour.status[d.status]}
                    </span>
                    {d.status === "open" && (
                      <button
                        data-testid={`tour-book-${d.iso}`}
                        onClick={() => scrollToId("booking")}
                        className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-red-500 hover:text-white border border-red-600/60 hover:bg-red-600 px-3 py-1.5 transition-colors duration-300"
                      >
                        {t.tour.request}
                      </button>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1} className="mt-8">
          <p className="font-mono-tech text-xs tracking-[0.2em] uppercase text-zinc-500">
            {t.tour.missing}{" "}
            <button data-testid="tour-cta-booking" onClick={() => scrollToId("booking")} className="text-red-500 hover:text-red-400 underline underline-offset-4">
              {t.tour.askCta}
            </button>{" "}
            {t.tour.flexible}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
